import { useEffect, useLayoutEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { useAuthUser } from "@shared/lib/auth";
import type { TourStepDef } from "./tourSteps";

type Rect = { x: number; y: number; width: number; height: number };

type TourStepProps = {
  step: TourStepDef;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onBack: () => void;
  onFinish: () => void;
  onAcknowledge?: () => void;
  acknowledging?: boolean;
  isFirst: boolean;
  isLast: boolean;
};

const PADDING = 8;
const CORNER_RADIUS = 12;

function useTargetRect(target: string | null): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    if (!target) {
      setRect(null);
      return;
    }

    const measure = () => {
      const el = document.querySelector(`[data-tour="${target}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({
          x: r.x - PADDING,
          y: r.y - PADDING,
          width: r.width + PADDING * 2,
          height: r.height + PADDING * 2,
        });
      }
    };

    // Measure immediately and after a short delay (for layout settling)
    measure();
    const timer = setTimeout(measure, 100);

    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [target]);

  return rect;
}

type TooltipPosition = "bottom" | "top" | "left" | "right";

function getTooltipPosition(rect: Rect, tooltipWidth: number, tooltipHeight: number): { pos: TooltipPosition; x: number; y: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const gap = 12;

  // Try bottom first
  if (rect.y + rect.height + gap + tooltipHeight < vh) {
    return {
      pos: "bottom",
      x: Math.max(16, Math.min(rect.x + rect.width / 2 - tooltipWidth / 2, vw - tooltipWidth - 16)),
      y: rect.y + rect.height + gap,
    };
  }

  // Try top
  if (rect.y - gap - tooltipHeight > 0) {
    return {
      pos: "top",
      x: Math.max(16, Math.min(rect.x + rect.width / 2 - tooltipWidth / 2, vw - tooltipWidth - 16)),
      y: rect.y - gap - tooltipHeight,
    };
  }

  // Try right
  if (rect.x + rect.width + gap + tooltipWidth < vw) {
    return {
      pos: "right",
      x: rect.x + rect.width + gap,
      y: Math.max(16, Math.min(rect.y + rect.height / 2 - tooltipHeight / 2, vh - tooltipHeight - 16)),
    };
  }

  // Fall back to left
  return {
    pos: "left",
    x: Math.max(16, rect.x - gap - tooltipWidth),
    y: Math.max(16, Math.min(rect.y + rect.height / 2 - tooltipHeight / 2, vh - tooltipHeight - 16)),
  };
}

export function TourStep({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onBack,
  onFinish,
  onAcknowledge,
  acknowledging,
  isFirst,
  isLast,
}: TourStepProps) {
  const targetRect = useTargetRect(step.target);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipSize, setTooltipSize] = useState({ width: 340, height: 180 });

  useLayoutEffect(() => {
    if (tooltipRef.current) {
      const { offsetWidth, offsetHeight } = tooltipRef.current;
      setTooltipSize({ width: offsetWidth, height: offsetHeight });
    }
  }, [stepIndex]);

  const { dataUseAcknowledgment } = useAuthUser();
  const isCenterModal = step.target === null;
  const isAcknowledgmentStep = step.requiresAcknowledgment === true;
  const acknowledgmentText = isAcknowledgmentStep ? (dataUseAcknowledgment?.text ?? step.description) : step.description;

  // For targeted steps, compute tooltip position
  const tooltipPos = targetRect
    ? getTooltipPosition(targetRect, tooltipSize.width, tooltipSize.height)
    : null;

  // Scroll the target element into view
  useEffect(() => {
    if (step.target) {
      const el = document.querySelector(`[data-tour="${step.target}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [step.target]);

  // Acknowledgment step has a distinct, wider layout
  if (isAcknowledgmentStep) {
    const acknowledgmentContent = (
      <>
        {/* Step counter */}
        <div className="flex items-center gap-2 mb-4">
          <div className="flex gap-1">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                className="h-1 rounded-full transition-all duration-300"
                style={{
                  width: i === stepIndex ? 20 : 6,
                  backgroundColor: i === stepIndex ? "#2D5A4A" : "hsl(var(--border))",
                }}
              />
            ))}
          </div>
          <span className="text-xs text-muted ml-auto">
            {stepIndex + 1} of {totalSteps}
          </span>
        </div>

        {/* Title with shield icon */}
        <div className="flex items-center gap-2 mb-3">
          <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2D5A4A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <h3 id="data-use-ack-title" className="text-lg font-semibold font-display text-foreground">
            {step.title}
          </h3>
        </div>

        {/* Acknowledgment text — prominently displayed */}
        <div className="bg-warning-bg border border-warning-text/20 rounded-lg p-4 mb-4 max-h-[40vh] overflow-y-auto">
          {acknowledgmentText.split("\n\n").map((paragraph, i) => (
            <p key={i} className="text-sm text-warning-text leading-relaxed mb-2 last:mb-0">
              {paragraph}
            </p>
          ))}
        </div>

        {/* Navigation — "I Acknowledge" replaces "Next" */}
        <div className="flex items-center justify-between">
          {!isFirst ? (
            <button
              onClick={onBack}
              className="px-4 py-2 text-sm font-medium text-muted hover:text-foreground transition-colors rounded-lg hover:bg-muted/10"
            >
              Back
            </button>
          ) : (
            <div />
          )}
          <button
            onClick={onAcknowledge}
            disabled={acknowledging}
            className="px-5 py-2.5 text-sm font-semibold text-white rounded-lg transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: "#2D5A4A" }}
          >
            {acknowledging ? "Saving..." : "I Acknowledge"}
          </button>
        </div>
      </>
    );

    return createPortal(
      <>
        {/* SVG overlay */}
        <svg
          className="fixed inset-0 z-[60] pointer-events-none"
          style={{ width: "100vw", height: "100vh" }}
        >
          <rect width="100%" height="100%" fill="rgba(0, 0, 0, 0.6)" />
        </svg>

        {/* Clickable backdrop */}
        <div className="fixed inset-0 z-[61]" />

        {/* Wider centered modal for acknowledgment */}
        <div className="fixed inset-0 z-[62] flex items-center justify-center">
          <motion.div
            ref={tooltipRef}
            key={stepIndex}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="data-use-ack-title"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="w-[480px] max-w-[calc(100vw-32px)] bg-surface rounded-2xl shadow-xl border border-border/50 p-6"
          >
            {acknowledgmentContent}
          </motion.div>
        </div>
      </>,
      document.body
    );
  }

  // Standard tour step (unchanged)
  const tooltipContent = (
    <>
      {/* Step counter */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex gap-1">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div
              key={i}
              className="h-1 rounded-full transition-all duration-300"
              style={{
                width: i === stepIndex ? 20 : 6,
                backgroundColor: i === stepIndex ? "#2D5A4A" : "hsl(var(--border))",
              }}
            />
          ))}
        </div>
        <span className="text-xs text-muted ml-auto">
          {stepIndex + 1} of {totalSteps}
        </span>
      </div>

      {/* Content */}
      <h3 className="text-lg font-semibold font-display text-foreground mb-1">
        {step.title}
      </h3>
      <p className="text-sm text-muted leading-relaxed mb-4">
        {step.description}
      </p>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        {!isFirst ? (
          <button
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-muted hover:text-foreground transition-colors rounded-lg hover:bg-muted/10"
          >
            Back
          </button>
        ) : (
          <div />
        )}
        <button
          onClick={isLast ? onFinish : onNext}
          className="px-5 py-2 text-sm font-medium text-white rounded-lg transition-all hover:-translate-y-0.5"
          style={{ backgroundColor: "#2D5A4A" }}
        >
          {isLast ? "Get started" : "Next"}
        </button>
      </div>
    </>
  );

  return createPortal(
    <>
      {/* SVG overlay with spotlight cutout */}
      <svg
        className="fixed inset-0 z-[60] pointer-events-none"
        style={{ width: "100vw", height: "100vh" }}
      >
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.x}
                y={targetRect.y}
                width={targetRect.width}
                height={targetRect.height}
                rx={CORNER_RADIUS}
                ry={CORNER_RADIUS}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.6)"
          mask="url(#tour-mask)"
        />
      </svg>

      {/* Clickable backdrop (prevents clicking behind) */}
      <div className="fixed inset-0 z-[61]" />

      {/* Tooltip */}
      {isCenterModal ? (
        <div className="fixed inset-0 z-[62] flex items-center justify-center">
          <motion.div
            ref={tooltipRef}
            key={stepIndex}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="w-[340px] max-w-[calc(100vw-32px)] bg-surface rounded-2xl shadow-xl border border-border/50 p-5"
          >
            {tooltipContent}
          </motion.div>
        </div>
      ) : (
        <motion.div
          ref={tooltipRef}
          key={stepIndex}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="fixed z-[62] w-[340px] max-w-[calc(100vw-32px)] bg-surface rounded-2xl shadow-xl border border-border/50 p-5"
          style={
            tooltipPos
              ? { left: tooltipPos.x, top: tooltipPos.y }
              : { left: "50%", top: "50%", transform: "translate(-50%, -50%)" }
          }
        >
          {tooltipContent}
        </motion.div>
      )}
    </>,
    document.body
  );
}
